import React from 'react';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import Fab from '@material-ui/core/Fab';
import TextField from '@material-ui/core/TextField';
import AddIcon from '@material-ui/icons/Add';
import GoogleMapReact, { Coords } from 'google-map-react';
import debounce from 'lodash/debounce';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AddLocation } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { Link as RouterLink, match, RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../../components/app';
import CustomizedTreeView from '../../../../components/customTree/customTree';
import ErrorMessage from '../../../../components/error-message';
import MyLink from '../../../../components/mylink/mylink';
import { TooltipButton } from '../../../../components/tooltip-button';
import ButtonTo from '../../../../components/buttonTo';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import { actions as pricingActions, selectors as pricingSelectors } from '../../../../redux/reducers/pricing';
import { Area, RootArea, TParams } from '../../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../../utils/global-styles';
import MapUtils from '../../../../utils/mapUtils';
import StringUtils from '../../../../utils/stringUtils';
import UserUtils, { AccountRight, RightRank } from '../../../../utils/userUtils';
import styles from './areas-list-style';
import { paths } from '../../../paths';
import { clsx } from '../../../../utils/htmlUtils';
import envConfig from '../../../../config';

const queryString = require('query-string');

// Typing must not re-render the tree (hundreds of rows) nor the map (which
// remeasures itself and forces a layout). Both only re-render when the props
// below actually change identity, so keystrokes cost a TextField render.
const MemoizedTreeView = React.memo(CustomizedTreeView);

// Stable reference, so closing everything does not break the memoization above
const NO_EXPANDED_IDS: string[] = [];

interface AreasMapProps {
    className: string;
    mapRef: React.RefObject<HTMLDivElement>;
    center: Coords;
    zoom: number;
    onApiLoaded: (maps: { map: any; maps: any }) => void;
}

const AreasMap = React.memo(({ className, mapRef, center, zoom, onApiLoaded }: AreasMapProps) => (
    <div className={className} ref={mapRef}>
        <GoogleMapReact
            bootstrapURLKeys={{ key: envConfig.google_key }}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={onApiLoaded}
            defaultCenter={center}
            defaultZoom={zoom}
        />
    </div>
));

interface PricingAreasListProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    classes: any;
    center: Coords;
    zoom: number;
    areas_tree: RootArea;
    getRootAreaOrDeleteError: ErrorPayload;
    getRootTreeAreas: () => void;
    deleteArea: (id: string) => void;
}

interface PricingAreasListState {
    filterText: string;
    needle: string;
    // Open / close all overrides the expansion the filter computes, until the filter changes
    expandOverride: 'all' | 'none' | null;
    expansionKey: number;
}

interface FilteredTree {
    children: Area[];
    needle: string;
    datas: Area[];
    expandedIds: string[];
}

class PricingAreasListPage extends React.Component<PricingAreasListProps, PricingAreasListState> {
    map: any;
    maps: any;
    mapWrapper: React.RefObject<HTMLDivElement>;
    mapWrapperFixed: boolean;

    currentArea: any;
    subAreas: any[];
    isAdmin: boolean;
    filteredTree: FilteredTree;
    allBranchIds: { datas: Area[]; ids: string[] };
    normalizedNames: WeakMap<Area, string>;

    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    constructor(props) {
        super(props);
        this.mapWrapper = React.createRef();
        this.handleScroll = this.handleScroll.bind(this);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.PRICING_MGMT, mode: RightRank.W });
        this.normalizedNames = new WeakMap();
        this.state = {
            filterText: '',
            needle: '',
            expandOverride: null,
            expansionKey: 0,
        };
    }

    componentDidMount() {
        this.props.getRootTreeAreas();
        document.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        document.removeEventListener('scroll', this.handleScroll);
        this.applyFilter.cancel();
    }

    handleScroll() {
        // Make google map fixed on scroll
        const mapDiv = this.mapWrapper.current;
        if (mapDiv) {
            const minPos = 10;
            const mapDivPosition = mapDiv.getBoundingClientRect();
            if (!mapDivPosition.width) {
                // Mobile view, map is invisible
                return;
            }
            if (this.mapWrapperFixed) {
                // Map has been fixed, check if we have rescrolled to top
                const mapDivCtnPosition = (mapDiv.parentNode as HTMLDivElement).getBoundingClientRect();
                if (mapDivCtnPosition.y > mapDivPosition.y) {
                    mapDiv.classList.remove(this.props.classes.mapWrapperFixed);
                    mapDiv.style.left = mapDivPosition.x + 'px';
                    mapDiv.style.top = '';
                    mapDiv.style.width = '';
                    mapDiv.style.height = '';
                    this.mapWrapperFixed = false;
                }
            } else {
                // Map isn't fixed, check if we have scrolled enough to bottom
                if (mapDivPosition.y < minPos) {
                    mapDiv.classList.add(this.props.classes.mapWrapperFixed);
                    mapDiv.style.left = mapDivPosition.x + 'px';
                    mapDiv.style.top = minPos + 'px';
                    mapDiv.style.width = mapDivPosition.width + 'px';
                    mapDiv.style.height = mapDivPosition.height + 'px';
                    this.mapWrapperFixed = true;
                }
            }
        }
    }

    handleRootArea = (obj, e): void => {
        this.props.history.push(`${paths.pricingManageAreasAdd}?id=${obj.id}`);
    };

    attachPolygonInfoWindow = (map, maps, obj, polygon): void => {
        const infoWindow = new maps.InfoWindow();
        maps.event.addListener(polygon, 'mouseover', function (e) {
            infoWindow.setContent(`${obj.name ?? obj.id}`);
            infoWindow.setPosition(e.latLng);
            infoWindow.open(map);
        });
        maps.event.addListener(polygon, 'click', this.handleRootArea.bind(this, obj));
    };

    handleApiLoaded = ({ map, maps }): void => {
        this.map = map;
        this.maps = maps;
    };

    handleDeleteArea = (area: Area) => (e) => {
        e.stopPropagation();
        this.props.deleteArea(area.id);
    };

    handleCenterMap =
        (area: Area, showSubAreas: boolean = false) =>
        (e) => {
            e.stopPropagation();
            const { map, maps } = this;
            if (map && maps && area.polygon) {
                if (this.currentArea) {
                    this.currentArea.setMap(null);
                    this.currentArea = null;
                }
                if (this.subAreas) {
                    this.subAreas.forEach((c) => {
                        c.setMap(null);
                        c = null;
                    });
                }
                this.subAreas = [];
                const bounds = new maps.LatLngBounds();
                MapUtils.extendBounds(bounds, area.polygon);

                this.currentArea = MapUtils.generatePolygon(maps, area.polygon);
                this.currentArea.setMap(map);
                if (!showSubAreas) {
                    this.attachPolygonInfoWindow(map, maps, area, this.currentArea);
                }

                map.fitBounds(bounds);

                if (showSubAreas && area.children) {
                    area.children.forEach((c) => {
                        MapUtils.extendBounds(bounds, c.polygon);

                        const subArea = MapUtils.generatePolygon(maps, c.polygon, '#0000FF');
                        subArea.setMap(map);
                        this.attachPolygonInfoWindow(map, maps, c, subArea);
                        this.subAreas.push(subArea);

                        map.fitBounds(bounds);
                    });
                }
            }
        };

    handleChangeUrl = (area: Area) => (e) => {
        this.props.history.push(`${paths.pricingManageAreas}?id=${area.id}`);
    };

    renderRow = (area: Area): JSX.Element => {
        const { t, globalClasses } = this.props;
        const { id, name, priority } = area;
        const hasPriority = Number(priority) > 0;

        return (
            <div key={id} style={{ display: 'flex', flex: 1, alignItems: 'center', marginTop: 18, marginBottom: 18 }}>
                {this.isAdmin ? (
                    <MyLink
                        style={{ flex: 3 }}
                        color="secondary"
                        onClick={this.handleChangeUrl(area)}
                        to={`${paths.pricingManageAreasAdd}?id=${id}`}
                    >
                        {name}
                    </MyLink>
                ) : (
                    <div style={{ flex: 3 }}>{name}</div>
                )}
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    {hasPriority && (
                        <TooltipButton title={`Priority ${priority}`}>
                            <Icon fontSize="small" style={{ color: '#ef6c00' }}>
                                flag
                            </Icon>
                        </TooltipButton>
                    )}
                </div>
                {area.children?.length > 0 && (
                    <TooltipButton title={t('areas.center_map_subs')}>
                        <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            variant="contained"
                            color="secondary"
                            onClick={this.handleCenterMap(area, true)}
                            className={globalClasses.btnAutoWidth}
                        >
                            <Icon fontSize="small">apps</Icon>
                        </Button>
                    </TooltipButton>
                )}
                <TooltipButton title={t('areas.center_map')}>
                    <Button
                        size="small"
                        style={{ marginLeft: 8 }}
                        variant="contained"
                        color="secondary"
                        onClick={this.handleCenterMap(area)}
                        className={globalClasses.btnAutoWidth}
                    >
                        <Icon fontSize="small">location_on</Icon>
                    </Button>
                </TooltipButton>
                {this.isAdmin && (
                    <TooltipButton title={t('areas.add_child_area')}>
                        <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            variant="contained"
                            color="secondary"
                            component={RouterLink}
                            to={`${paths.pricingManageAreasAdd}?parent_id=${area.id}`}
                            className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                        >
                            <Icon fontSize="small">add</Icon>
                        </Button>
                    </TooltipButton>
                )}
                {/* <Dialog
                    btnTitle={t('common:validate')}
                    description={t('common:deleteParam', {
                        id: `(${id}) ${name}`,
                    })}
                    customButton={
                        <TooltipButton title={t('common:delete')}>
                            <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                variant="contained"
                                color="primary"
                                className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                            >
                                <Icon fontSize="small">delete</Icon>
                            </Button>
                        </TooltipButton>
                    }
                    onOK={this.handleDeleteArea(area)}
                /> */}
            </div>
        );
    };

    getNormalizedName = (node: Area): string => {
        const cached = this.normalizedNames.get(node);
        if (cached !== undefined) {
            return cached;
        }
        const normalized = StringUtils.normalizeForSearch(node.name || '');
        this.normalizedNames.set(node, normalized);
        return normalized;
    };

    // Keep a node when its label matches the filter or any of its descendants match,
    // so nested matches stay reachable in the tree. A matching node keeps all its
    // children, so centering the map on its sub areas still shows them all.
    filterTree = (nodes: Area[], needle: string, expandedIds: string[]): Area[] => {
        const filtered: Area[] = [];
        for (const node of nodes) {
            if (this.getNormalizedName(node).includes(needle)) {
                // The node itself matches: keep its whole subtree so centering the
                // map on its sub areas still shows them all, but leave it collapsed
                // (expanding every match would mount the entire tree at once).
                filtered.push(node);
                continue;
            }
            const children = node.children ? this.filterTree(node.children, needle, expandedIds) : [];
            if (children.length > 0) {
                // Only kept because a descendant matches, so open it to reveal them.
                expandedIds.push(node.id);
                filtered.push({ ...node, children });
            }
        }
        return filtered;
    };

    // Filtering walks the whole tree, so cache the result and reuse it while
    // neither the tree nor the search term changed. The stable references also
    // let the memoized tree skip re-rendering on unrelated updates.
    getFilteredTree = (children: Area[], needle: string): FilteredTree => {
        if (this.filteredTree?.children === children && this.filteredTree.needle === needle) {
            return this.filteredTree;
        }
        const expandedIds = [];
        const sourceNodes =
            needle &&
            this.filteredTree?.children === children &&
            this.filteredTree?.needle &&
            needle.startsWith(this.filteredTree.needle)
                ? this.filteredTree.datas
                : children;
        const datas = needle ? this.filterTree(sourceNodes, needle, expandedIds) : children;
        this.filteredTree = { children, needle, datas, expandedIds };
        return this.filteredTree;
    };

    applyFilter = debounce((filterText: string): void => {
        // The filter decides again which branches are open, whatever was asked before
        this.setState({ needle: StringUtils.normalizeForSearch(filterText.trim()), expandOverride: null });
    }, 300);

    handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        this.setState({ filterText: value });
        this.applyFilter(value);
    };

    // Every branch of the displayed tree. Cached on the node list identity: the array is a
    // prop of the memoized tree, rebuilding it on each keystroke would re-render it all.
    getAllBranchIds = (datas: Area[]): string[] => {
        if (this.allBranchIds?.datas !== datas) {
            this.allBranchIds = { datas, ids: this.collectBranchIds(datas) };
        }
        return this.allBranchIds.ids;
    };

    collectBranchIds = (nodes: Area[]): string[] =>
        nodes.reduce((acc, node) => {
            if (node.children?.length > 0) {
                acc.push(node.id, ...this.collectBranchIds(node.children));
            }
            return acc;
        }, []);

    handleToggleAll = (): void => {
        // The tree is uncontrolled, so the key has to change for the new expansion to apply
        this.setState((previous) => ({
            expandOverride: previous.expandOverride === 'all' ? 'none' : 'all',
            expansionKey: previous.expansionKey + 1,
        }));
    };

    renderAreaTree = (): JSX.Element => {
        const { location, areas_tree: areaTree } = this.props;
        const { needle, expandOverride, expansionKey } = this.state;
        if (!areaTree?.children) {
            return null;
        }
        const params = queryString.parse(location.search);
        const { datas, expandedIds } = this.getFilteredTree(areaTree.children, needle);

        return (
            <MemoizedTreeView
                datas={datas}
                onDoubleClick={this.handleCenterMap}
                expandId={needle || expandOverride ? undefined : params.id}
                expandedIds={
                    expandOverride === 'all'
                        ? this.getAllBranchIds(datas)
                        : expandOverride === 'none'
                          ? NO_EXPANDED_IDS
                          : expandedIds
                }
                expansionKey={expansionKey}
                customRow={this.renderRow}
            />
        );
    };

    renderFAB = (): JSX.Element => {
        const { t, globalClasses } = this.props;
        return (
            this.isAdmin && (
                <>
                    <br />
                    <br />
                    <Fab
                        className={globalClasses.fabBotRight}
                        size="large"
                        component={RouterLink}
                        to={paths.pricingManageAreasAdd}
                        color="secondary"
                        aria-label={t('common:add')}
                    >
                        <AddIcon />
                    </Fab>
                </>
            )
        );
    };

    render() {
        const { t, globalClasses, classes, center, zoom, getRootAreaOrDeleteError, areas_tree: areaTree } = this.props;
        const { filterText, expandOverride } = this.state;
        const payload = areaTree || {};
        const allExpanded = expandOverride === 'all';

        return (
            <App
                needRights={AccountRight.PRICING_MGMT}
                loading={!(payload && payload.children)}
                header={{
                    title: t('areas.root_areas'),
                    button: (
                        <ButtonTo
                            rights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.W }}
                            startIcon={<AddLocation />}
                            to={paths.pricingManageAreasAdd}
                            text={t('common:add')}
                        />
                    ),
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <ErrorMessage payload={getRootAreaOrDeleteError} />
                    {payload && payload.children && (
                        <>
                            {payload.children.length === 0 && (
                                <p>
                                    <b>{t('areas.no_areas')}</b>
                                </p>
                            )}
                            <div style={{ display: 'flex', flex: 1 }}>
                                <div className={globalClasses.block} style={{ flex: 1, marginRight: 40 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            label={t('areas.filter_areas')}
                                            value={filterText}
                                            onChange={this.handleFilterChange}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={this.handleToggleAll}
                                            style={{ whiteSpace: 'nowrap' }}
                                            startIcon={<Icon>{allExpanded ? 'unfold_less' : 'unfold_more'}</Icon>}
                                        >
                                            {allExpanded ? t('areas.collapse_all') : t('areas.expand_all')}
                                        </Button>
                                    </div>
                                    {this.renderAreaTree()}
                                </div>
                                <div
                                    className={globalClasses.blockMargin}
                                    style={{ height: '80vh', width: '100%', flex: 1, overflow: 'hidden' }}
                                >
                                    <AreasMap
                                        className={classes.mapWrapper}
                                        mapRef={this.mapWrapper}
                                        center={center}
                                        zoom={zoom}
                                        onApiLoaded={this.handleApiLoaded}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {/* {this.renderFAB()} */}
                </div>
            </App>
        );
    }
}

const PricingAreasListPageWithTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['pricing', 'common'])(PricingAreasListPage)),
);

const mapStateToProps = (state) => ({
    areas: pricingSelectors.root_areas(state),
    areas_tree: pricingSelectors.root_areas_tree(state),
    getRootAreaOrDeleteError: errorSelectors.getRootAreaOrDeleteError(state),
});

const mapDispatchToProps = {
    getRootAreas: pricingActions.getRootAreas,
    getRootTreeAreas: pricingActions.getRootTreeAreas,
    deleteArea: pricingActions.deleteArea,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingAreasListPageWithTranslation);
